import { Link, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { Menu, X, Search, ShoppingBag, User, Infinity } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import api from "../lib/api";

const NAV = [
 { label: "Marketing", to: "/#marketing" },
 { label: "Consulting", to: "/#consulting" },
 { label: "Entertainment ↗", to: "https://eoninfinityentertainment.com/", external: true },
 { label: "Boerboels", to: "/boerboels" },
 { label: "Contact", to: "/contact" },
];

const SEARCH_KEYS = ["dogs", "breeds", "breeders", "products", "courses"];

function DesktopNav() {
  return (
    <nav className="hidden xl:flex items-center gap-6">
      {NAV.map((n) => (
        <a key={n.to} href={n.to} target={n.external ? "_blank" : undefined} rel={n.external ? "noopener noreferrer" : undefined} aria-label={n.external ? "Entertainment (external site, opens a new tab)" : n.label} data-testid={`nav-${n.to.slice(1)}`}
          className="text-sm text-eon-text/80 hover:text-eon-gold transition-colors duration-200 whitespace-nowrap">
          {n.label}
        </a>
      ))}
    </nav>
  );
}

function UserActions({ user, logout, nav }) {
  if (user) {
    return (
      <div className="hidden lg:flex items-center gap-3">
        <Link to={user.role === "admin" ? "/admin" : "/dashboard"} data-testid="nav-dashboard-btn"
          className="flex items-center gap-2 text-sm text-eon-text/80 hover:text-eon-gold transition-colors">
          <User size={18} /> {user.name?.split(" ")[0]}
        </Link>
        <button data-testid="nav-logout-btn" onClick={() => { logout(); nav("/"); }}
          className="text-xs text-eon-muted hover:text-eon-text transition-colors uppercase tracking-wider">Logout</button>
      </div>
    );
  }
  return (
    <div className="hidden lg:flex items-center gap-3">
      <Link to="/login" data-testid="nav-login-btn"
        className="text-sm font-semibold text-eon-text/80 hover:text-eon-gold transition-colors px-2">
        Sign In
      </Link>
      <Link to="/contact" data-testid="nav-getstarted-btn"
        className="inline-block bg-eon-gold text-white text-sm font-bold tracking-wide px-5 py-2.5 rounded-full hover:bg-eon-goldhover transition-colors duration-200">
        Discuss Your Project
      </Link>
    </div>
  );
}

function MobileMenu({ user, logout, nav, setOpen }) {
  return (
    <div className="xl:hidden border-t border-eon-border bg-eon-bg px-5 py-4 space-y-1">
      {NAV.map((n) => (
        <a key={n.to} href={n.to} target={n.external ? "_blank" : undefined} rel={n.external ? "noopener noreferrer" : undefined} aria-label={n.external ? "Entertainment (external site, opens a new tab)" : n.label} onClick={() => setOpen(false)}
          className="block py-2.5 text-eon-text/80 hover:text-eon-gold transition-colors">{n.label}</a>
      ))}
      <div className="pt-3 border-t border-eon-border mt-3">
        {user ? (
          <>
            <Link to={user.role === "admin" ? "/admin" : "/dashboard"} onClick={() => setOpen(false)} className="block py-2.5 text-eon-gold">Dashboard</Link>
            <button onClick={() => { logout(); setOpen(false); nav("/"); }} className="block py-2.5 text-eon-muted">Logout</button>
          </>
        ) : (
          <Link to="/login" onClick={() => setOpen(false)} className="block py-2.5 text-eon-gold font-bold">Sign In</Link>
        )}
      </div>
    </div>
  );
}

function SearchOverlay({ q, setQ, results, onClose, goSearch }) {
  const pathFor = (k, r) =>
    k === "dogs" ? `/dogs/${r.id}` : k === "breeds" ? `/breeds/${r.slug}` :
    k === "breeders" ? `/breeders/${r.id}` : k === "products" ? `/shop/${r.id}` : `/academy/${r.id}`;
  const empty = results && SEARCH_KEYS.every((k) => !(results[k] || []).length);
  return (
    <div className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm" onClick={onClose}>
      <div className="max-w-2xl mx-auto mt-24 px-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 bg-eon-surface border border-eon-border px-4">
          <Search size={20} className="text-eon-gold" />
          <input autoFocus data-testid="global-search-input" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search dogs, breeds, breeders, products, courses..."
            className="flex-1 bg-transparent py-4 text-eon-text outline-none placeholder:text-eon-muted" />
          <button onClick={onClose}><X size={20} className="text-eon-muted" /></button>
        </div>
        {results && (
          <div className="bg-eon-surface border border-t-0 border-eon-border max-h-[60vh] overflow-auto divide-y divide-eon-border">
            {SEARCH_KEYS.flatMap((k) =>
              (results[k] || []).map((r) => (
                <button key={`${k}-${r.id || r.slug}`} onClick={() => goSearch(pathFor(k, r))} data-testid={`search-result-${k}-${r.id || r.slug}`}
                  className="w-full flex items-center gap-3 p-3 hover:bg-eon-elevated transition-colors text-left">
                  <img src={r.image} alt="" className="w-10 h-10 object-cover" />
                  <div>
                    <div className="text-sm text-eon-text">{r.name || r.title}</div>
                    <div className="eon-label">{k}</div>
                  </div>
                </button>
              ))
            )}
            {empty && <div className="p-6 text-center text-eon-muted text-sm">No results found</div>}
          </div>
        )}
      </div>
    </div>
  );
}

export function Navbar() {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const nav = useNavigate();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);

  useEffect(() => {
    if (!q.trim()) { setResults(null); return; }
    const t = setTimeout(async () => {
      try { const { data } = await api.get(`/search?q=${encodeURIComponent(q)}`); setResults(data); }
      catch { setResults(null); }
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  const goSearch = (path) => { setSearchOpen(false); setQ(""); nav(path); };

  return (
    <header className="corporate-header eon-glass sticky top-0 z-50">
      <div className="max-w-[1400px] mx-auto px-5 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5 shrink-0">
            <span className="w-9 h-9 lg:w-10 lg:h-10 rounded-xl bg-eon-gold flex items-center justify-center"><Infinity size={20} className="text-white" strokeWidth={2.75} /></span>
            <span className="leading-none">
              <span className="block font-display font-black text-lg lg:text-xl tracking-tight text-eon-text">E.O.N</span>
              <span className="block text-[9px] font-bold tracking-[0.25em] text-eon-muted mt-0.5">INFINITY INC</span>
            </span>
          </Link>

          <DesktopNav />

          <div className="flex items-center gap-3 lg:gap-4">
            {pathname !== "/" && <><button aria-label="Search E.O.N." data-testid="nav-search-btn" onClick={() => setSearchOpen(true)}
              className="text-eon-text/80 hover:text-eon-gold transition-colors">
              <Search size={20} />
            </button>
            <Link to="/cart" aria-label="Shopping cart" data-testid="nav-cart-btn" className="relative text-eon-text/80 hover:text-eon-gold transition-colors">
              <ShoppingBag size={20} />
              {count > 0 && <span className="absolute -top-2 -right-2 bg-eon-gold text-white text-[10px] font-bold w-4 h-4 flex items-center justify-center rounded-full">{count}</span>}
            </Link>
            </>}<UserActions user={user} logout={logout} nav={nav} />
            <button className="xl:hidden text-eon-text" aria-label={open ? "Close navigation" : "Open navigation"} aria-expanded={open} data-testid="nav-mobile-toggle" onClick={() => setOpen(!open)}>
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </div>

      {open && <MobileMenu user={user} logout={logout} nav={nav} setOpen={setOpen} />}

      {searchOpen && (
        <SearchOverlay q={q} setQ={setQ} results={results} onClose={() => setSearchOpen(false)} goSearch={goSearch} />
      )}
    </header>
  );
}
